import { type ChartOptions } from "chart.js";
import { compact, orderBy } from "lodash";
import moment from "moment";

import { type DataPoint } from "@features/overview-v2/components/tile/utils";
import { isIncomplete } from "@features/sales-v3/charts/plugins/chart-data-manager-plugin";
import { generateDateLabels } from "@features/sales-v3/utils/chart";
import { DesignElementManager } from "@hooks/use-design-element-manager";
import { type User } from "@internal/user";
import {
  type GetLandingPageReportChart_me_landingPageReportChart_rows,
  type SalesPageMetric,
} from "@north-beam/nb-client-side";
import {
  convertStartDateToTimeZone,
  type CurrencyCode,
  type DateInterval,
  type ITimeGranularityV2,
  type metrics,
} from "@north-beam/nb-common";

import { chartTooltip } from "./chart-tooltip";

type GetChartOptonsParams = {
  timeGranularity: ITimeGranularityV2;
  alignAxes: boolean;
  code: CurrencyCode;
  data: ReturnType<typeof generateChartDataset>;
};

export function getChartOptions({
  timeGranularity,
  alignAxes,
  data,
  code,
}: GetChartOptonsParams): ChartOptions<any> {
  const activeDatasets = data.datasets.filter((e) => !e.hidden);

  const maxYValue = Math.max(
    ...compact(activeDatasets.flatMap((e) => e.data).flatMap((e) => e.y)),
  );

  const scales: Record<string, any> = {
    x: {
      grid: {
        drawOnChartArea: false,
        drawBorder: false,
        drawTicks: false,
      },
      ticks: {
        padding: 12,
        maxRotation: 20,
        autoSkip: true,
        font: {
          size: 11,
        },
      },
      type: "timeseries",
      time: {
        unit: (
          {
            hourly: "day",
            daily: "day",
            weekly: "week",
            monthly: "month",
          } as const
        )[timeGranularity],
      },
    },
  };

  data.datasets.forEach((dataset, index) => {
    const isFirstScale = activeDatasets.indexOf(dataset) === 0;
    scales[`y${index}`] = {
      position: isFirstScale ? "left" : "right",
      display: !dataset.hidden,
      beginAtZero: true,
      grid: {
        display: isFirstScale,
        drawTicks: false,
        drawBorder: false,
        drawOnChartArea: true,
      },
      ticks: {
        display: activeDatasets.length < 4 && !dataset.hidden,
        maxTicksLimit: 6,
        padding: 3,
        font: {
          size: 11,
        },
        callback: (value: number | string) =>
          dataset.metric.formatter(Number(value), code, {
            precision: "approximate",
          }),
      },
      title: {
        display: activeDatasets.length <= 3,
      },
      ...(alignAxes ? { suggestedMax: maxYValue } : null),
    };
  });

  return {
    responsive: true,
    maintainAspectRatio: false,
    parsing: false,
    animations: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
      position: "nearest",
    },
    plugins: {
      tooltip: {
        enabled: false,
        external: chartTooltip,
        callbacks: {
          label(context: any) {
            return {
              datasetIndex: context.datasetIndex,
              label: context.dataset.name,
              landingPage: context.dataset.landingPage,
              adName: context.dataset.adName,
              dateRange: context.parsed.dateRange,
              value: context.dataset.metric.formatter(context.parsed.y, code, {
                precision: "approximate",
              }),
            };
          },
          labelColor(context: any) {
            return {
              backgroundColor: context.dataset.labelColor,
              borderDash: context.dataset.borderDash,
            };
          },
        },
      },
      legend: {
        display: false,
      },
    },
    barThickness: "flex",
    minBarLength: 3,
    layout: {
      padding: { top: 36, bottom: 36 },
    },
    scales,
  };
}

export function generateChartData({
  metricsData,
  metrics,
  activeMetrics,
  timeGranularity,
  dateRange,
  timezone,
}: {
  metricsData: GetLandingPageReportChart_me_landingPageReportChart_rows[];
  metrics: SalesPageMetric[];
  activeMetrics: string[];
  timeGranularity: ITimeGranularityV2;
  dateRange: DateInterval;
  timezone: User["timezone"];
}) {
  const today = moment().startOf("day");
  const datasets = [];

  const labels = generateDateLabels(dateRange, timezone, timeGranularity);

  for (const metric of metrics) {
    for (const row of metricsData) {
      const dataset: {
        data: DataPoint[];
        name: string;
        landingPage: string;
        adName: string;
        metric: SalesPageMetric;
        hidden: boolean;
      } = {
        data: [],
        name:
          metric.metadata.overviewPageMetricName ||
          metric.metadata.name ||
          metric.name,
        landingPage: row.landingPage,
        adName: row.name,
        metric,
        hidden: !activeMetrics.includes(metric.id),
      };

      const sortedMetrics = orderBy(row.metrics, ["date"], "asc");

      for (const [index, dailyMetrics] of sortedMetrics.entries()) {
        const date = moment(
          convertStartDateToTimeZone(dailyMetrics.date, timezone),
        );

        let value = 0;

        if (metric.baseMetrics) {
          const agg: metrics.MetricPoint = {} as metrics.MetricPoint;
          metric.baseMetrics.forEach((m) => {
            agg[m.id as Exclude<keyof metrics.MetricPoint, "convs" | "goals">] =
              m.calculate(dailyMetrics);
          });
          value = metric.calculate(agg);
        } else {
          value = metric.calculate(dailyMetrics);
        }

        let y = isNaN(value) ? 0 : value ?? 0;

        if (Math.abs(y) < 1e-6 || y === Infinity) {
          y = 0;
        }

        dataset.data.push({
          x: labels[index],
          y: date.isAfter(today) ? null : y,
          dateRange: dailyMetrics.dateRange,
        });
      }

      datasets.push(dataset);
    }
  }

  return { labels: labels, datasets };
}

const designElementManager = new DesignElementManager();

export function generateChartDataset(
  data: ReturnType<typeof generateChartData>,
  dateRange: DateInterval,
  timeGranularity: ITimeGranularityV2,
) {
  const datasets = [];

  for (const [i, dataset] of data.datasets.entries()) {
    const singleDataPoint = dataset.data.length === 1;
    const color = designElementManager.getColorForIndex(i);
    const colorTransparent = color + "50";
    const colorBackdrop = color + "20";
    const colorBackdropTransparent = color + "10";

    const isIncompleteDate = (date: Date, index: number) => {
      return isIncomplete({
        date,
        dateRange,
        timeGranularity,
        index,
        total: data.labels.length,
      });
    };

    datasets.push({
      ...dataset,
      id: i,
      yAxisID: `y${i}`,
      pointRadius: singleDataPoint ? 3 : 0,
      pointHoverRadius: 5,
      pointHitRadius: 100,
      backgroundColor: color,
      borderColor: color,
      hoverBackgroundColor: color,
      hoverBorderColor: color,
      borderWidth: 3,
      segment: {
        backgroundColor: (ctx: any) =>
          isIncompleteDate(
            ctx.p0.parsed.dateRange.startDate,
            ctx.p0DataIndex,
          ) ||
          isIncompleteDate(ctx.p1.parsed.dateRange.startDate, ctx.p1DataIndex)
            ? colorBackdropTransparent
            : colorBackdrop,
        borderColor: (ctx: any) =>
          isIncompleteDate(
            ctx.p0.parsed.dateRange.startDate,
            ctx.p0DataIndex,
          ) ||
          isIncompleteDate(ctx.p1.parsed.dateRange.startDate, ctx.p1DataIndex)
            ? colorTransparent
            : color,
      },
      labelColor: color,
    });
  }

  return { ...data, datasets };
}
